import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Mail, RefreshCw, CheckCircle2, AlertCircle, Send, Users, Calendar, MapPin, Clock, Eye, Edit3, ChevronDown,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { AllergyFormCompact } from '../../components/AllergyFormCompact';
import { GuestFormsDialog } from '../../components/GuestFormsDialog';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusPill = (status, rsvp) => {
  const s = (rsvp || status || '').toLowerCase();
  if (s === 'yes') return { label: 'Yes', color: '#06D6A0' };
  if (s === 'no') return { label: 'No', color: '#E5484D' };
  if (s === 'opened') return { label: 'Opened', color: '#F59E0B' };
  if (s === 'sent') return { label: 'Sent', color: '#6366F1' };
  if (s === 'failed') return { label: 'Failed', color: '#E5484D' };
  return { label: 'Pending', color: '#6B6B7A' };
};

export const CrmInvitationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState({ invitation: null, recipients: [] });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [allergyOpen, setAllergyOpen] = useState(false);
  const [allergyTarget, setAllergyTarget] = useState(null);
  const [allergyExisting, setAllergyExisting] = useState(null);
  const [allergyReadOnly, setAllergyReadOnly] = useState(false);
  const [busy, setBusy] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/invitations/${id}`, { withCredentials: true });
      setData(res.data || { invitation: null, recipients: [] });
    } catch {
      toast.error('Could not load invitation');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [id]);

  const invitation = data.invitation;
  const [formsModalOpen, setFormsModalOpen] = useState(false);
  const recipients = data.recipients || [];

  const pendingAllergyYesCount = useMemo(
    () => recipients.filter(r => (r.rsvp_response || '').toLowerCase() === 'yes' && !r.allergy_form_completed).length,
    [recipients],
  );
  const pendingRsvpCount = useMemo(
    () => recipients.filter(r => !['yes', 'no'].includes((r.rsvp_response || '').toLowerCase())).length,
    [recipients],
  );

  const toggleSelect = (rid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(rid)) next.delete(rid); else next.add(rid);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === recipients.length) setSelected(new Set());
    else setSelected(new Set(recipients.map(r => r.recipient_id)));
  };

  const openAllergyEdit = async (recipient, readOnly = false) => {
    setAllergyTarget(recipient);
    setAllergyReadOnly(readOnly);
    setAllergyExisting(null);
    try {
      const res = await axios.get(`${API_URL}/api/admin/invitation-recipients/${recipient.recipient_id}/allergy-form`, { withCredentials: true });
      setAllergyExisting(res.data?.form || null);
    } catch {
      setAllergyExisting(null);
    }
    setAllergyOpen(true);
  };

  const submitAllergy = async (payload) => {
    if (!allergyTarget) return;
    try {
      await axios.post(
        `${API_URL}/api/admin/invitation-recipients/${allergyTarget.recipient_id}/allergy-form`,
        payload,
        { withCredentials: true },
      );
      toast.success(`Allergy form saved for ${allergyTarget.name || 'guest'}`);
      setAllergyOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const resendInvitations = async (onlyPending = false) => {
    if (!invitation) return;
    setBusy(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/invitations/${invitation.invitation_id}/send`,
        { only_pending: onlyPending },
        { withCredentials: true },
      );
      toast.success(`Sent: ${res.data?.sent || 0} · Failed: ${res.data?.failed || 0}`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Resend failed');
    } finally {
      setBusy(false);
    }
  };

  const remindAllergy = async () => {
    if (!invitation) return;
    setBusy(true);
    try {
      const payload = selected.size > 0
        ? { recipient_ids: Array.from(selected) }
        : { only_yes: true };
      const res = await axios.post(
        `${API_URL}/api/admin/invitations/${invitation.invitation_id}/remind-allergy`,
        payload,
        { withCredentials: true },
      );
      toast.success(`Reminders sent: ${res.data?.sent || 0} of ${res.data?.total_pending || 0}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reminder failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="py-12 flex items-center justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-[#FF3B6B]" />
      </div>
    );
  }

  if (!invitation) {
    return (
      <div className="text-center py-12">
        <p className="text-[#6B6B7A] mb-3">Invitation not found.</p>
        <Link to="/crm/invitations" className="text-[#FF3B6B] font-semibold text-sm">← Back to invitations</Link>
      </div>
    );
  }

  const allSelected = selected.size === recipients.length && recipients.length > 0;

  return (
    <div className="space-y-6 max-w-6xl" data-testid="crm-invitation-detail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/crm/invitations')} className="text-[#6B6B7A]" data-testid="back-invitations">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h1 className="text-2xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>
            {invitation.child_name}'s invitation
          </h1>
        </div>
      </div>

      {/* Invitation summary */}
      <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-5">
          {invitation.photo_url && (
            <div className="md:col-span-1">
              <img
                src={`${API_URL}${invitation.photo_url}`}
                alt={invitation.child_name}
                className="w-full h-48 object-cover rounded-xl"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          )}
          <div className={`space-y-2 ${invitation.photo_url ? 'md:col-span-2' : 'md:col-span-3'}`}>
            <div className="flex items-center gap-3 text-sm">
              <Badge variant="outline" className="border-[#E8E5DF] text-[#6B6B7A]">Age {invitation.age}</Badge>
              <span className="flex items-center gap-1 text-[#1E1E24]"><Calendar className="w-3.5 h-3.5" /> {invitation.party_date}</span>
              <span className="flex items-center gap-1 text-[#1E1E24]"><Clock className="w-3.5 h-3.5" /> {invitation.party_time}</span>
              <span className="flex items-center gap-1 text-[#1E1E24]"><MapPin className="w-3.5 h-3.5" /> {invitation.location || 'TBD'}</span>
            </div>
            <p className="text-sm text-[#6B6B7A] italic mt-1">{invitation.message || 'Come celebrate with us!'}</p>
            <div className="flex items-center gap-2 pt-2">
              <span className="text-xs text-[#6B6B7A]">Hosted by</span>
              <span className="text-sm font-medium text-[#1E1E24]">{invitation.owner_name}</span>
              <span className="text-xs text-[#6B6B7A]">· {invitation.owner_email}</span>
              {invitation.booking_id && (
                <Badge className="bg-[#06D6A0]/15 text-[#06D6A0] border-none text-[10px]" data-testid="linked-booking-badge">
                  Linked booking
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total invited" value={recipients.length} color="#1E1E24" icon={Users} />
        <StatCard label="RSVP pending" value={pendingRsvpCount} color="#F59E0B" icon={Clock} />
        <StatCard label="Pending allergy forms" value={pendingAllergyYesCount} color="#E5484D" icon={AlertCircle} testId="pending-allergy-count" />
        <StatCard label="Forms completed" value={recipients.filter(r => r.allergy_form_completed).length + (recipients.length ? `/${recipients.length}` : '')} color="#06D6A0" icon={CheckCircle2}
          testId="forms-completed-stat"
          onClick={invitation?.booking_id ? () => setFormsModalOpen(true) : undefined} />
      </div>

      <GuestFormsDialog
        open={formsModalOpen}
        onOpenChange={setFormsModalOpen}
        title={`Completed forms — ${invitation?.child_name || 'party'}`}
        pdfUrlFor={(f) => `${API_URL}/api/admin/waivers/${f.form_id}/pdf`}
        loader={async () => {
          const res = await axios.get(`${API_URL}/api/admin/bookings/${invitation.booking_id}/waiver-forms`, {
            params: { invitation_id: invitation.invitation_id }, withCredentials: true,
          });
          return res.data;
        }}
      />

      {/* Bulk actions */}
      <div className="flex flex-wrap gap-2">
        <Button
          onClick={() => resendInvitations(true)}
          disabled={busy || recipients.filter(r => !['sent', 'opened', 'yes', 'no'].includes((r.status || '').toLowerCase())).length === 0}
          className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white text-xs"
          data-testid="send-pending-btn"
          title="Send invitation email to recipients with status 'pending'"
        >
          <Mail className="w-3.5 h-3.5 mr-1.5" /> Send pending invites
        </Button>
        <Button
          variant="outline"
          onClick={() => resendInvitations(false)}
          disabled={busy || recipients.length === 0}
          className="rounded-full border-[#E8E5DF] text-xs"
          data-testid="resend-all-btn"
          title="Resend invitation email to ALL recipients (including those who already received it)"
        >
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Resend all
        </Button>
        <Button
          variant="outline"
          onClick={remindAllergy}
          disabled={busy || pendingAllergyYesCount === 0}
          className="rounded-full border-[#E8E5DF] text-xs"
          data-testid="remind-allergy-btn"
          title={selected.size > 0 ? `Send allergy reminder to ${selected.size} selected` : `Send allergy reminder to ${pendingAllergyYesCount} recipient(s) who RSVP'd YES but haven't filled the form`}
        >
          <Send className="w-3.5 h-3.5 mr-1.5" /> {selected.size > 0 ? `Remind ${selected.size} selected` : `Remind ${pendingAllergyYesCount} pending`}
        </Button>
      </div>

      {!invitation.booking_id && (
        <div className="bg-[#FFF7EB] border border-[#F5C15A] rounded-2xl p-4 text-xs text-[#8A5A1F]">
          <strong>No booking linked.</strong> Allergy forms require a linked booking. Ask the host to connect this invitation to their booking from the customer portal, or add a booking_id from the Bookings page.
        </div>
      )}

      {/* Recipients table */}
      <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8">
                <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} data-testid="select-all-recipients" className="rounded accent-[#FF3B6B]" />
              </TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>RSVP</TableHead>
              <TableHead>Allergy form</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="recipients-table-body">
            {recipients.map((r) => {
              const s = statusPill(r.status, r.rsvp_response);
              const canFillAllergy = !!invitation.booking_id;
              return (
                <TableRow key={r.recipient_id} data-testid={`recipient-row-${r.recipient_id}`}>
                  <TableCell>
                    <input
                      type="checkbox"
                      checked={selected.has(r.recipient_id)}
                      onChange={() => toggleSelect(r.recipient_id)}
                      className="rounded accent-[#FF3B6B]"
                      data-testid={`select-${r.recipient_id}`}
                    />
                  </TableCell>
                  <TableCell>
                    <p className="font-medium text-[#1E1E24]">{r.name || '—'}</p>
                    {r.allergy_filled_by && (
                      <p className="text-[10px] text-[#6B6B7A] uppercase">by {r.allergy_filled_by}</p>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-[#6B6B7A]">{r.email || '—'}</TableCell>
                  <TableCell className="text-xs text-[#6B6B7A]">{r.phone || '—'}</TableCell>
                  <TableCell>
                    <Badge style={{ background: `${s.color}18`, color: s.color, border: 'none' }}>{s.label}</Badge>
                  </TableCell>
                  <TableCell className="text-xs">
                    {r.rsvp_response === 'yes' ? <span className="text-[#06D6A0] font-semibold">Yes</span>
                      : r.rsvp_response === 'no' ? <span className="text-[#E5484D] font-semibold">No</span>
                      : <span className="text-[#6B6B7A]">—</span>}
                  </TableCell>
                  <TableCell>
                    {r.allergy_form_completed ? (
                      <Badge className="bg-[#06D6A0]/15 text-[#06D6A0] border-none text-[11px]" data-testid={`allergy-done-${r.recipient_id}`}>
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Completed
                      </Badge>
                    ) : (
                      <Badge className="bg-[#FFEFD1] text-[#8A5A1F] border-none text-[11px]" data-testid={`allergy-pending-${r.recipient_id}`}>
                        <AlertCircle className="w-3 h-3 mr-1" /> Pending
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {r.allergy_form_completed ? (
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openAllergyEdit(r, true)}
                          className="h-8 w-8 p-0"
                          title="View submitted allergy form"
                          data-testid={`view-allergy-${r.recipient_id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openAllergyEdit(r, false)}
                          disabled={!canFillAllergy}
                          className="h-8 w-8 p-0 text-[#6366F1] hover:bg-[#6366F1]/10"
                          title="Edit allergy form"
                          data-testid={`edit-allergy-${r.recipient_id}`}
                        >
                          <Edit3 className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => openAllergyEdit(r, false)}
                        disabled={!canFillAllergy}
                        className="h-8 bg-[#FF3B6B] hover:bg-[#E62E58] text-white text-xs rounded-full px-3"
                        title={canFillAllergy ? 'Fill allergy & consent form on behalf of this guest' : 'Requires a linked booking'}
                        data-testid={`fill-allergy-${r.recipient_id}`}
                      >
                        Complete Form
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {recipients.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-sm text-[#6B6B7A]">No recipients yet.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Allergy form dialog */}
      <Dialog open={allergyOpen} onOpenChange={setAllergyOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="allergy-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {allergyReadOnly ? <Eye className="w-5 h-5 text-[#6B6B7A]" /> : <Edit3 className="w-5 h-5 text-[#FF3B6B]" />}
              Allergy & consent — {allergyTarget?.name || 'guest'}
            </DialogTitle>
            <DialogDescription>
              {allergyReadOnly
                ? 'Viewing the submitted form (read-only).'
                : 'Fill on behalf of the guest. This typically happens when the parent called to share info.'}
              {allergyExisting?.filled_by && (
                <span className="ml-2 text-[10px] uppercase bg-[#FFFDF8] px-2 py-0.5 rounded-full">by {allergyExisting.filled_by}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          {allergyTarget && (
            <AllergyFormCompact
              initial={allergyExisting}
              attendeeName={allergyExisting?.attendee_name || allergyTarget?.name || ''}
              defaultSignatureName={user?.name || ''}
              readOnly={allergyReadOnly}
              onSubmit={submitAllergy}
              onCancel={() => setAllergyOpen(false)}
              submitLabel={allergyExisting ? 'Save changes' : 'Submit on behalf'}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatCard = ({ label, value, color, icon: Icon, testId, onClick }) => {
  const Wrap = onClick ? 'button' : 'div';
  return (
    <Wrap
      onClick={onClick}
      className={`bg-white rounded-2xl border border-[#E8E5DF] p-4 flex items-center gap-3 text-left w-full ${onClick ? 'hover:border-[#06D6A0] hover:shadow-sm transition-all cursor-pointer' : ''}`}
      data-testid={testId}
    >
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}18` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div>
        <p className="text-[11px] uppercase font-semibold text-[#6B6B7A] tracking-wider">{label}</p>
        <p className="text-xl font-bold text-[#1E1E24]">{value}</p>
      </div>
    </Wrap>
  );
};

export default CrmInvitationDetail;
